import React from "react";

import {
  PageList,
  ENUM_FORM_DISPLAY,
  ENUM_FORM_LAYOUT_CONTAINER,
} from "../components/pageList";

import { useCellRender } from "../hooks/useCellRender";
import { useFormatter } from "../hooks/useFomatter";

import ActionCellRender from "../components/actionCellRender";

import { IconTrash, IconPencil, IconSettings } from "@tabler/icons";
import { Checkbox, Textarea, Badge } from "@mantine/core";
import _ from "lodash";
import AIRequestSummary from "../components/aiRequestSummary";
import AIRequestLogMessages from "../components/aiRequestLogMessages";
import AIRequestPayload from "../components/aiRequestPayload";
import AIRequestOutput from "../components/aiRequestOutput";
import moment from "moment";
import AIRequestErrorSelect from "../components/aiRequestErrorSelect";
import AIRequestDocOutputs from "../components/aiRequestDocOutputs";
import AIRequestPayloadEmailViewer from "../components/aiRequestPayloadEmailViewer";

const AIRequest = () => {
  const apiEntity = "aiRequest";
  const initialValues = {};
  const [cellRender] = useCellRender();
  const tableSchema = {
    showActionColumn: true,
    columns: [
      {
        field: "code",
        headerName: "Code",
        cellRender: (col, data) => cellRender.AIRequestCode(data),
      },
      // { field: "type", headerName: "Type" },
      // { field: "status", headerName: "Status" },
      // { field: "description", headerName: "Task" },

      {
        field: "outputs",
        headerName: "Task & Outputs",
        cellRender: (col, data) => cellRender.AIRequestOutputs(data),
      },
      {
        field: "updatedAt",
        headerName: "Updated At",
        cellRender: (col, data) =>
          moment(data.updatedAt).format("YYYY-MM-DD HH:mm:ss"),
      },
      {
        field: "approved",
        headerName: "Approval",
        cellRender: (col, data) => (
          <Badge
            color={data.approved ? "green" : data.completed ? "blue" : "red"}
            size="xs"
            radius={0}
          >
            {data.approved
              ? "Approved"
              : data.completed
              ? "Completed"
              : "Reviewing"}
          </Badge>
        ),
      },
      {
        field: "action",
        cellRender: ActionCellRender,
        isAction: true,
        actions: [
          {
            name: "edit",
            icon: IconPencil,
            props: {
              //   color:"red"
            },
          },
        ],
      },
    ],
    searchableFields: ["status", "searchText", "code"],
    select:
      "code status type description createdAt updatedAt approved completed remark outputs payload.email.subject",
  };
  const formSchema = {
    display: {
      mode: ENUM_FORM_DISPLAY.LINK,
    },
    title: "AI Request",
    titleField: "code",
    apiEntity,
    initialValues,
    layout: {
      containers: [
        //tabs
        {
          key: "tab-main",
          parent: null,
          type: ENUM_FORM_LAYOUT_CONTAINER.TABS,
          props: {
            variant: "default", //default | pills | outline
            defaultValue: "Basic Info",
          },
          tab: [
            {
              key: "tab-basic-info",
              label: "Basic Info",
            },
            {
              key: "tab-approval",
              label: "Approval",
            },
          ],
        },
        {
          key: "section-summary",
          parent: "tab-basic-info",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          props: {
            title: "AI Request Basic Info",
            withBorder: false,
            mt: 0,
            mb: "md",
          },
        },
        {
          key: "section-output",
          parent: "tab-basic-info",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          props: {
            title: "Output",
            withBorder: false,
            mt: 0,
            mb: "md",
          },
        },
        {
          key: "section-payload",
          parent: "tab-basic-info",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          props: {
            title: "Payload",
            withBorder: false,
            mt: 0,
            mb: "md",
          },
        },
        {
          key: "section-log-messages",
          parent: "tab-basic-info",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          props: {
            title: "Log Messages",
            withBorder: false,
            mt: 0,
            mb: "md",
          },
        },
        {
          key: "section-approval",
          parent: "tab-approval",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          props: {
            title: "User Approval",
            withBorder: false,
            mt: 0,
            mb: "md",
          },
        },
        {
          key: "section-approval-output",
          parent: "tab-approval",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          props: {
            title: "Document Outputs",
            withBorder: false,
            mt: 0,
            mb: "md",
          },
        },
        {
          key: "section-approval-payload-email",
          parent: "tab-approval",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          visible: (form) => {
            return !_.isEmpty(form.values?.payload?.email);
          },
          props: {
            title: "Payload Email",
            withBorder: false,
            mt: 0,
            mb: "md",
          },
        },
      ],
      fields: [
        {
          key: "field-code",
          parent: "section-summary",
          component: AIRequestSummary,
          props: {},
        },
        {
          key: "field-output",
          parent: "section-output",
          component: AIRequestOutput,
          props: {},
        },
        {
          key: "field-payload",
          parent: "section-payload",
          component: AIRequestPayload,
          props: {},
        },
        {
          key: "field-log-messages",
          parent: "section-log-messages",
          component: AIRequestLogMessages,
          props: {},
        },
        //checkbox
        {
          name: "approved",
          parent: "section-approval",
          component: Checkbox,
          props: {
            label: "Approved By Operation Team",
            type: "checkbox",
            variant: "filled",
          },
        },
        {
          name: "completed",
          parent: "section-approval",
          component: Checkbox,
          props: {
            label: "Completed",
            type: "checkbox",
            variant: "filled",
          },
        },
        {
          name: "foundErrors",
          parent: "section-approval",
          component: AIRequestErrorSelect,
          props: {
            label: "Found Errors",
          },
        },
        {
          name: "remark",
          parent: "section-approval",
          component: Textarea,
          props: {
            label: "Remark",
          },
        },
        {
          name: "docOutputs",
          parent: "section-approval-output",
          component: AIRequestDocOutputs,
          props: {
            label: "Document Outputs",
          },
        },
        {
          name: "payloadEmailViewer",
          parent: "section-approval-payload-email",
          component: AIRequestPayloadEmailViewer,
          props: {
            // label: "Payload Email",
          },
        },
      ],
    },
  };

  return (
    <PageList
      title="AI Request"
      apiEntity={apiEntity}
      initialValues={initialValues}
      tableSchema={tableSchema}
      formSchema={formSchema}
      showAddButton={false}
    />
  );
};

export default AIRequest;
